import React, { useEffect, useRef } from 'react';
import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import { Ticket } from '../../types/ticket';
import TicketGridCard from './TicketGridCard';
import { ParkingLocation } from '../../types/parkingLocation';

interface Props {
	tickets: Ticket[];
	isLoadingInitial: boolean;
	isLoadingMore: boolean;
	hasMore: boolean;
	loadMore: () => void;
	onTicketClick: (ticket: Ticket) => void;
	parkingLocation?: ParkingLocation;
	emptyMessage?: string;
}

const SKELETON_COUNT = 8;

const gridSx = {
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
	gap: 2,
	width: '100%',
};

const TicketsGrid: React.FC<Props> = ({
	tickets,
	isLoadingInitial,
	isLoadingMore,
	hasMore,
	loadMore,
	onTicketClick,
	parkingLocation,
	emptyMessage = 'No records found.',
}) => {
	const sentinelRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const node = sentinelRef.current;
		if (!node) return;
		if (!hasMore) return;
		const obs = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) loadMore();
			},
			{ rootMargin: '300px 0px' },
		);
		obs.observe(node);
		return () => obs.disconnect();
	}, [hasMore, loadMore]);

	if (isLoadingInitial) {
		return (
			<Box sx={gridSx}>
				{Array.from({ length: SKELETON_COUNT }).map((_, i) => (
					<Skeleton
						key={i}
						variant="rectangular"
						height={340}
						sx={{ borderRadius: 1 }}
					/>
				))}
			</Box>
		);
	}

	if (tickets.length === 0) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					py: 6,
				}}
			>
				<Typography variant="body2" color="text.secondary">
					{emptyMessage}
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ width: '100%' }}>
			<Box sx={gridSx}>
				{tickets.map((ticket) => (
					<TicketGridCard
						key={ticket.ticketNumber}
						ticket={ticket}
						onClick={onTicketClick}
						parkingLocation={parkingLocation}
					/>
				))}
			</Box>
			<Box ref={sentinelRef} sx={{ height: 1, width: '100%' }} aria-hidden />
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					gap: 1,
					py: 2,
					minHeight: 48,
				}}
			>
				{isLoadingMore && (
					<>
						<CircularProgress size={18} />
						<Typography variant="body2" color="text.secondary">
							Loading more…
						</Typography>
					</>
				)}
				{!isLoadingMore && !hasMore && tickets.length > 0 && (
					<Typography variant="caption" color="text.secondary">
						End of list ({tickets.length} record
						{tickets.length === 1 ? '' : 's'})
					</Typography>
				)}
			</Box>
		</Box>
	);
};

export default TicketsGrid;
